/**
 * Action Communication Center — Phase 1.
 *
 * Operational discussion threads with status, priority, participants, and
 * activity timeline. Two-pane layout: left = thread list with filters,
 * right = selected thread (messages + activity timeline).
 *
 * Components are co-located here intentionally — keeps the feature self-
 * contained while it stabilises in Phase 1.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  MessageSquare, Plus, AlertTriangle, Clock, CheckCircle2, Circle,
  XCircle, Send, Users, Search, X, UserPlus, RefreshCw,
} from 'lucide-react';

const PRIORITY_META = {
  low:      { label: 'Low',      cls: 'text-[#5C6B64] bg-[#F3F5F2]' },
  medium:   { label: 'Medium',   cls: 'text-[color:var(--color-primary)] bg-[#EAF4EE]' },
  high:     { label: 'High',     cls: 'text-[#D4A017] bg-[#FEF6E0]' },
  critical: { label: 'Critical', cls: 'text-[#C45A41] bg-[#FEF2EE]' },
};
const STATUS_META = {
  open:        { label: 'Open',        cls: 'text-[color:var(--color-primary)] bg-[#EAF4EE]', Icon: Circle },
  in_progress: { label: 'In progress', cls: 'text-[#D4A017] bg-[#FEF6E0]', Icon: Clock },
  waiting:     { label: 'Waiting',     cls: 'text-[#5C6B64] bg-[#F3F5F2]', Icon: Clock },
  resolved:    { label: 'Resolved',    cls: 'text-[#2D6A4F] bg-[#EAF4EE]', Icon: CheckCircle2 },
  closed:      { label: 'Closed',      cls: 'text-[#5C6B64] bg-[#F3F5F2]', Icon: XCircle },
};
const LINK_META = {
  petty_cash:        'Petty Cash',
  reservation:       'Reservation',
  customer_feedback: 'Customer Feedback',
  production_plan:   'Production',
  maintenance:       'Maintenance',
  checklist:         'Checklist',
  general:           'General',
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  if (diffMin < 4320) return `${Math.floor(diffMin / 1440)}d ago`;
  return d.toLocaleDateString();
};

const errMsg = (e, fallback = 'Action failed') => {
  const detail = e?.response?.data?.detail ?? e?.message ?? null;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => (d?.msg || JSON.stringify(d))).join(' · ') || fallback;
  return detail.msg || JSON.stringify(detail);
};

// ────────────────────────── Create Thread Modal ──────────────────────────
function CreateThreadModal({ open, onClose, onCreated, outlets, users }) {
  const { getHeaders, API } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [outletId, setOutletId] = useState('');
  const [priority, setPriority] = useState('medium');
  const [linkKind, setLinkKind] = useState('general');
  const [participantIds, setParticipantIds] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle(''); setDescription(''); setOutletId(''); setPriority('medium');
    setLinkKind('general'); setParticipantIds([]); setSearchUser('');
  };
  useEffect(() => { if (!open) reset(); }, [open]);

  const filteredUsers = useMemo(() => {
    const q = searchUser.trim().toLowerCase();
    if (!q) return users.slice(0, 30);
    return users.filter(u => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [users, searchUser]);

  const submit = async () => {
    if (!title.trim()) { return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/discussions`, {
        title: title.trim(),
        description: description.trim(),
        outlet_id: outletId || null,
        priority,
        link_kind: linkKind,
        participant_ids: participantIds,
      }, { headers: getHeaders() });
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(errMsg(e, 'Failed to create thread'));
    } finally {
      setSubmitting(false);
    }
  };

  const togglePart = (uid) => {
    setParticipantIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="discussion-create-modal">
        <DialogHeader>
          <DialogTitle>New discussion thread</DialogTitle>
          <DialogDescription>Open a structured conversation linked to an outlet, module, or operational item.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold">Title</label>
            <Input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. AC repair at Bell Tower – urgent"
              data-testid="discussion-title-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold">Description (optional)</label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)}
              rows={3} placeholder="Add context — what's the issue / what action is needed?"
              data-testid="discussion-desc-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold">Outlet</label>
              <select value={outletId} onChange={e => setOutletId(e.target.value)}
                data-testid="discussion-outlet-select"
                className="h-8 w-full rounded-md border border-[#E2E8DF] px-2 text-xs bg-white">
                <option value="">— No outlet —</option>
                {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                data-testid="discussion-priority-select"
                className="h-8 w-full rounded-md border border-[#E2E8DF] px-2 text-xs bg-white">
                {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold">Link to</label>
              <select value={linkKind} onChange={e => setLinkKind(e.target.value)}
                data-testid="discussion-linkkind-select"
                className="h-8 w-full rounded-md border border-[#E2E8DF] px-2 text-xs bg-white">
                {Object.entries(LINK_META).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold flex items-center gap-1">
              <Users className="w-3 h-3" /> Participants ({participantIds.length})
            </label>
            <div className="relative mt-1">
              <Search className="w-3 h-3 absolute left-2 top-2.5 text-[#5C6B64]" />
              <Input value={searchUser} onChange={e => setSearchUser(e.target.value)}
                placeholder="Search users…" className="h-7 pl-7 text-xs"
                data-testid="discussion-user-search" />
            </div>
            <div className="mt-1 max-h-40 overflow-y-auto border border-[#E2E8DF] rounded-md divide-y divide-[#E2E8DF]/60">
              {filteredUsers.length === 0 ? (
                <p className="text-[11px] text-[#5C6B64] text-center py-3">No matches</p>
              ) : filteredUsers.map(u => (
                <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-[#F9F9F8] cursor-pointer text-xs"
                  data-testid={`discussion-user-row-${u.id}`}>
                  <input type="checkbox" checked={participantIds.includes(u.id)}
                    onChange={() => togglePart(u.id)} />
                  <span className="font-medium text-[#1A2421]">{u.name}</span>
                  <span className="text-[10px] text-[#5C6B64]">· {u.role || '—'}</span>
                  <span className="text-[10px] text-[#9CA3AF] ml-auto">{u.email}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={submitting || !title.trim()}
              data-testid="discussion-create-submit"
              className="bg-[color:var(--color-primary)] hover:bg-[#0F2A20] text-white">
              {submitting ? 'Creating…' : 'Create thread'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────── Thread Row ──────────────────────────
function ThreadRow({ thread, active, onSelect }) {
  const PMeta = PRIORITY_META[thread.priority] || PRIORITY_META.medium;
  const SMeta = STATUS_META[thread.status] || STATUS_META.open;
  const StatusIcon = SMeta.Icon;
  const unread = thread.unread_count > 0;
  return (
    <button type="button" onClick={() => onSelect(thread)}
      data-testid={`discussion-thread-row-${thread.id}`}
      className={`w-full text-left px-3 py-2.5 border-b border-[#EAEAEA] hover:bg-[#F9F9F8] transition-colors ${active ? 'bg-[#EAF4EE]' : 'bg-white'}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <h4 className={`text-xs font-semibold truncate ${unread ? 'text-[color:var(--color-primary)]' : 'text-[#1A2421]'}`}>{thread.title}</h4>
        {unread && <span className="bg-[#C45A41] text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 tabular-nums shrink-0">{thread.unread_count}</span>}
      </div>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider flex-wrap">
        <span className={`px-1.5 py-0.5 rounded font-semibold ${SMeta.cls}`}>
          <StatusIcon className="w-2.5 h-2.5 inline-block mr-0.5" />{SMeta.label}
        </span>
        <span className={`px-1.5 py-0.5 rounded font-semibold ${PMeta.cls}`}>{PMeta.label}</span>
        {thread.link_kind && thread.link_kind !== 'general' && (
          <span className="px-1.5 py-0.5 rounded font-semibold text-[#5C6B64] bg-[#F3F5F2]">{LINK_META[thread.link_kind]}</span>
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] text-[#5C6B64] mt-1.5">
        <span className="truncate">{thread.created_by_name}</span>
        <span className="tabular-nums shrink-0">{fmtTime(thread.last_message_at)} · {thread.message_count}<MessageSquare className="w-2.5 h-2.5 inline-block ml-0.5" /></span>
      </div>
    </button>
  );
}

// ────────────────────────── Thread View (right pane) ──────────────────────────
function ThreadView({ threadId, onChanged, users, currentUserId }) {
  const { getHeaders, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addUserSearch, setAddUserSearch] = useState('');

  const load = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/discussions/${threadId}`, { headers: getHeaders() });
      setData(d);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [threadId, API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/discussions/${threadId}/messages`, { body: draft.trim(), mentions: [] },
        { headers: getHeaders() });
      setDraft('');
      load();
      onChanged?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(errMsg(e, 'Failed to post'));
    } finally {
      setPosting(false);
    }
  };

  const updateField = async (patch) => {
    try {
      await axios.patch(`${API}/discussions/${threadId}`, patch, { headers: getHeaders() });
      load();
      onChanged?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(errMsg(e, 'Update failed'));
    }
  };

  const addParticipant = async (uid) => {
    try {
      await axios.post(`${API}/discussions/${threadId}/participants`, { user_id: uid, role: 'member' },
        { headers: getHeaders() });
      load();
      setAddUserSearch('');
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(errMsg(e, 'Failed to add participant'));
    }
  };

  const removeParticipant = async (uid) => {
    try {
      await axios.delete(`${API}/discussions/${threadId}/participants/${uid}`, { headers: getHeaders() });
      load();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(errMsg(e, 'Failed to remove participant'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" /></div>;
  }
  if (!data) return <div className="p-8 text-center text-sm text-[#5C6B64]">Thread not found.</div>;

  const t = data.thread;
  const SMeta = STATUS_META[t.status] || STATUS_META.open;
  const PMeta = PRIORITY_META[t.priority] || PRIORITY_META.medium;
  const partIds = new Set((data.participants || []).map(p => p.user_id));
  const candidates = users.filter(u =>
    !partIds.has(u.id) &&
    (!addUserSearch.trim() || (u.name + u.email).toLowerCase().includes(addUserSearch.trim().toLowerCase()))
  ).slice(0, 8);

  return (
    <div className="flex flex-col h-full" data-testid="discussion-thread-view">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#E2E8DF] bg-white shrink-0">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-[#1A2421] truncate" data-testid="discussion-thread-title">{t.title}</h3>
            <p className="text-[10px] text-[#5C6B64] mt-0.5">
              by {t.created_by_name} · {fmtTime(t.created_at)}
              {t.link_kind && t.link_kind !== 'general' && <> · linked to <span className="font-semibold">{LINK_META[t.link_kind]}</span></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={t.status} onChange={e => updateField({ status: e.target.value })}
            data-testid="discussion-status-select"
            className={`h-6 rounded text-[10px] uppercase tracking-wider font-semibold px-2 border-0 ${SMeta.cls}`}>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={t.priority} onChange={e => updateField({ priority: e.target.value })}
            data-testid="discussion-priority-control"
            className={`h-6 rounded text-[10px] uppercase tracking-wider font-semibold px-2 border-0 ${PMeta.cls}`}>
            {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button type="button" onClick={() => setShowAdd(s => !s)}
            data-testid="discussion-add-user-toggle"
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-[color:var(--color-primary)] hover:bg-[#EAF4EE] rounded px-2 py-0.5 ml-auto">
            <UserPlus className="w-3 h-3" /> {data.participants.length}
          </button>
        </div>
      </div>

      {/* Add participant pop-out */}
      {showAdd && (
        <div className="bg-[#FAFBFA] border-b border-[#E2E8DF] px-4 py-2.5">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {data.participants.map(p => (
              <span key={p.user_id} className="inline-flex items-center gap-1 text-[10px] bg-white border border-[#E2E8DF] rounded-full px-2 py-0.5"
                data-testid={`discussion-participant-${p.user_id}`}>
                {p.name}<span className="text-[9px] text-[#9CA3AF]">· {p.role}</span>
                {p.user_id !== t.created_by && p.user_id !== currentUserId && (
                  <button type="button" onClick={() => removeParticipant(p.user_id)} className="hover:text-[#C45A41]"
                    data-testid={`discussion-participant-remove-${p.user_id}`}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
          <Input value={addUserSearch} onChange={e => setAddUserSearch(e.target.value)}
            placeholder="Add participant by name or email…" className="h-7 text-xs"
            data-testid="discussion-add-search" />
          {addUserSearch.trim() && candidates.length > 0 && (
            <div className="mt-1 border border-[#E2E8DF] rounded-md bg-white max-h-32 overflow-y-auto">
              {candidates.map(u => (
                <button key={u.id} type="button" onClick={() => addParticipant(u.id)}
                  data-testid={`discussion-add-candidate-${u.id}`}
                  className="w-full text-left px-2 py-1 text-xs hover:bg-[#EAF4EE] flex items-center gap-2">
                  <span className="font-medium">{u.name}</span>
                  <span className="text-[10px] text-[#9CA3AF]">{u.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#FAFBFA]" data-testid="discussion-messages-list">
        {(data.messages || []).length === 0 ? (
          <p className="text-xs text-[#5C6B64] text-center py-6">No messages yet — start the conversation below.</p>
        ) : data.messages.map(m => {
          const mine = m.author_id === currentUserId;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
              data-testid={`discussion-message-${m.id}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-1.5 ${mine ? 'bg-[color:var(--color-primary)] text-white' : 'bg-white border border-[#E2E8DF]'}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[10px] font-semibold ${mine ? 'text-white/80' : 'text-[color:var(--color-primary)]'}`}>{m.author_name}</span>
                  <span className={`text-[9px] ${mine ? 'text-white/60' : 'text-[#9CA3AF]'} tabular-nums`}>{fmtTime(m.created_at)}</span>
                </div>
                <p className={`text-xs leading-snug whitespace-pre-line break-words ${mine ? 'text-white' : 'text-[#1A2421]'}`}>{m.body}</p>
              </div>
            </div>
          );
        })}
        {/* Activity timeline (compact, below messages) */}
        {(data.activity || []).length > 0 && (
          <div className="border-t border-[#E2E8DF] pt-2 mt-3">
            <p className="text-[9px] uppercase tracking-wider text-[#5C6B64] font-semibold mb-1">Activity</p>
            <ul className="space-y-0.5">
              {data.activity.slice(0, 8).map(a => (
                <li key={a.id} className="text-[10px] text-[#5C6B64] tabular-nums">
                  <span className="font-semibold">{a.kind.replace(/_/g, ' ')}</span> · {fmtTime(a.at)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-[#E2E8DF] bg-white px-4 py-2.5 shrink-0">
        <div className="flex items-end gap-2">
          <Textarea value={draft} onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(); }}
            placeholder="Type a message… (Ctrl+Enter to send)"
            rows={2} className="resize-none text-xs" data-testid="discussion-composer-input" />
          <Button size="sm" onClick={send} disabled={posting || !draft.trim()}
            data-testid="discussion-composer-send"
            className="bg-[color:var(--color-primary)] hover:bg-[#0F2A20] text-white">
            <Send className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────── Main page ──────────────────────────
export default function ActionCommCenter() {
  const { getHeaders, API, user } = useAuth();
  const currentUserId = user?.id || user?._id || user?.email;
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState('open');     // open | mine | critical | closed
  const [search, setSearch] = useState('');
  const [outlets, setOutlets] = useState([]);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadOutlets = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/outlets`, { headers: getHeaders() });
      setOutlets(r.data || []);
    } catch { /* manager users may not have access — silent fallback */ }
  }, [API, getHeaders]);

  const loadUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/discussions/users/lookup`, { headers: getHeaders() });
      setUsers(r.data?.items || []);
    } catch { /* no-op */ }
  }, [API, getHeaders]);

  const loadThreads = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (tab === 'open') params.set('status', 'open');
      else if (tab === 'critical') params.set('priority', 'critical');
      else if (tab === 'closed') params.set('status', 'closed');
      else if (tab === 'mine') params.set('assigned_to_me', 'true');
      const r = await axios.get(`${API}/discussions?${params.toString()}`, { headers: getHeaders() });
      setThreads(r.data?.items || []);
    } catch { /* no-op */ } finally {
      setRefreshing(false);
    }
  }, [API, getHeaders, tab]);

  useEffect(() => { loadOutlets(); loadUsers(); }, [loadOutlets, loadUsers]);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const visibleThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? threads.filter(t => t.title.toLowerCase().includes(q)) : threads;
  }, [threads, search]);

  return (
    <div data-testid="action-comm-center-page" className="flex flex-col h-[calc(100vh-7rem)]">
      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-[#1A2421]">Action Communication Center</h2>
          <p className="text-sm text-[#5C6B64] mt-0.5">Operational discussions, escalations, and accountability — linked to outlets &amp; modules.</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}
          data-testid="discussion-new-btn"
          className="bg-[color:var(--color-primary)] hover:bg-[#0F2A20] text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> New thread
        </Button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[340px_1fr] gap-3">
        {/* Left pane */}
        <Card className="border-[#E2E8DF] shadow-sm flex flex-col min-h-0">
          <CardContent className="p-0 flex flex-col h-full">
            <div className="px-3 py-2 border-b border-[#E2E8DF] flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-3 h-3 absolute left-2 top-2 text-[#5C6B64]" />
                <Input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search threads…" className="h-7 pl-7 text-xs"
                  data-testid="discussion-search" />
              </div>
              <button type="button" onClick={loadThreads} disabled={refreshing}
                className="text-[#5C6B64] hover:text-[color:var(--color-primary)]" data-testid="discussion-refresh"
                title="Refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div className="flex p-1 gap-0.5 border-b border-[#E2E8DF] bg-[#F9F9F8]">
              {[
                { k: 'open',     label: 'Open' },
                { k: 'mine',     label: 'Mine' },
                { k: 'critical', label: 'Critical' },
                { k: 'closed',   label: 'Closed' },
              ].map(t => (
                <button key={t.k} type="button" onClick={() => setTab(t.k)}
                  data-testid={`discussion-tab-${t.k}`}
                  className={`flex-1 h-6 rounded text-[10px] font-semibold uppercase tracking-wide transition-colors ${tab === t.k ? 'bg-white shadow-sm text-[color:var(--color-primary)]' : 'text-[#5C6B64] hover:text-[#1A2421]'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="discussion-thread-list">
              {visibleThreads.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <AlertTriangle className="w-6 h-6 text-[#9CA3AF] mx-auto mb-2" />
                  <p className="text-xs text-[#5C6B64]">No threads in this view.</p>
                </div>
              ) : visibleThreads.map(t => (
                <ThreadRow key={t.id} thread={t} active={active?.id === t.id}
                  onSelect={(th) => setActive(th)} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Right pane */}
        <Card className="border-[#E2E8DF] shadow-sm overflow-hidden">
          <CardContent className="p-0 h-full">
            {active ? (
              <ThreadView threadId={active.id}
                currentUserId={currentUserId}
                users={users}
                onChanged={loadThreads} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center px-6 py-12">
                <MessageSquare className="w-10 h-10 text-[#9CA3AF] mb-3" />
                <h3 className="text-base font-semibold text-[#1A2421] mb-1">Select a thread</h3>
                <p className="text-xs text-[#5C6B64] max-w-sm">
                  Pick a discussion from the left, or start a new one to coordinate on outlet operations,
                  maintenance issues, customer escalations, or other action items.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateThreadModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(t) => { setActive(t); loadThreads(); }}
        outlets={outlets}
        users={users}
      />
    </div>
  );
}

// Lightweight notification bell — opens the page on click. Imported by Layout.
export function DiscussionsBell({ onClick, testId = 'discussion-bell' }) {
  // Hook needed for state isolation; safe to colocate.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { getHeaders, API } = useAuth();
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/discussions/notifications/unread-count`, { headers: getHeaders() });
      setCount(data?.unread_count || 0);
    } catch { /* unauth or unreachable — stay 0 */ }
  }, [API, getHeaders]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);   // poll every 60s — Phase 1 simple
    return () => clearInterval(id);
  }, [load]);

  return (
    <button type="button" onClick={onClick}
      data-testid={testId}
      title="Action Communication Center"
      className="relative inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#EAF4EE] text-[#5C6B64] hover:text-[color:var(--color-primary)] transition-colors">
      <MessageSquare className="w-4 h-4" />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 bg-[#C45A41] text-white text-[9px] font-bold rounded-full px-1 min-w-[14px] h-[14px] flex items-center justify-center"
          data-testid={`${testId}-count`}>
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}
